import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Badge, { type BadgeVariant } from '../ui/Badge';
import Button from '../ui/Button';
import Card from '../ui/Card';
import RecommendationPanel, { type RecommendationVariant } from '../ui/RecommendationPanel';
import type {
  ScanReviewResult,
  ScanReviewSignal,
  ScanReviewSignalSeverity,
  ScanReviewVerdict,
} from '../../lib/scan-review';

interface ScanReviewPanelProps {
  review: ScanReviewResult;
  compact?: boolean;
  showValidationHelper?: boolean;
  title?: string;
  description?: string;
  dataTestId?: string;
}

function verdictBadgeVariant(verdict: ScanReviewVerdict): BadgeVariant {
  switch (verdict) {
    case 'likely_reliable':
      return 'success';
    case 'review_recommended':
      return 'warning';
    case 'low_confidence':
    default:
      return 'danger';
  }
}

function verdictPanelVariant(verdict: ScanReviewVerdict): RecommendationVariant {
  switch (verdict) {
    case 'likely_reliable':
      return 'info';
    case 'review_recommended':
      return 'warning';
    case 'low_confidence':
    default:
      return 'danger';
  }
}

function signalBadgeVariant(severity: ScanReviewSignalSeverity): BadgeVariant {
  switch (severity) {
    case 'success':
      return 'success';
    case 'warning':
      return 'warning';
    case 'danger':
    default:
      return 'danger';
  }
}

function signalRecommendationVariant(severity: ScanReviewSignalSeverity): RecommendationVariant {
  switch (severity) {
    case 'success':
      return 'info';
    case 'warning':
      return 'warning';
    case 'danger':
    default:
      return 'danger';
  }
}

export default function ScanReviewPanel({
  review,
  compact = false,
  showValidationHelper = false,
  title,
  description,
  dataTestId,
}: ScanReviewPanelProps) {
  const { t } = useTranslation('common');
  const [copyState, setCopyState] = useState<'idle' | 'copying' | 'copied' | 'failed'>('idle');

  const verdictLabel = t(`review.badges.${review.verdict}`);
  const verdictTitle = t(`review.verdicts.${review.verdict}.title`);
  const verdictBody = t(`review.verdicts.${review.verdict}.body`);

  const displayedSignals = compact ? review.signals.slice(0, 3) : review.signals;
  const evidenceCoverage = formatCoverage(review.metrics.evidenceCoverage, t);
  const remediationCoverage = formatCoverage(review.metrics.remediationCoverage, t);
  const developerCoverage = formatCoverage(review.metrics.developerGuidanceCoverage, t);
  const selectorCoverage = formatCoverage(review.metrics.selectorCoverage, t);
  const domSnippetCoverage = formatCoverage(review.metrics.domSnippetCoverage, t);

  const accessValue = review.metrics.authMode === 'public'
    ? t('review.statValues.public')
    : t('review.statValues.authCoverage', {
        pages: review.metrics.authenticatedPagesScanned ?? 0,
        routes: review.metrics.discoveredRouteCount ?? 0,
      });

  const copySummary = useMemo(() => {
    const signalLines = displayedSignals.map((signal) => {
      const content = resolveSignalContent(signal, t);
      return `${content.title}: ${content.body}`;
    });

    return [
      `AbleComply ${t('review.title')}`,
      `${t('review.summary.verdict')}: ${verdictLabel}`,
      `${t('review.stats.pagesScanned')}: ${review.metrics.pagesScanned}${review.metrics.pagesFailed !== null ? ` (${review.metrics.pagesFailed} ${t('review.summary.pagesFailedSuffix')})` : ''}`,
      `${t('review.stats.issuesFound')}: ${t('review.statValues.issuesAcrossTypes', {
        issues: review.metrics.totalIssues,
        types: review.metrics.issueTypes,
      })}`,
      `${t('review.stats.severityMix')}: ${t('review.statValues.severitySummary', review.metrics.severityCounts)}`,
      `${t('review.stats.evidenceCoverage')}: ${evidenceCoverage}`,
      `${t('review.stats.remediationCoverage')}: ${remediationCoverage}`,
      `${t('review.stats.developerGuidanceCoverage')}: ${developerCoverage}`,
      `${t('review.stats.authContext')}: ${accessValue}`,
      `${t('review.summary.signals')}:`,
      ...signalLines.map((line) => `- ${line}`),
    ].join('\n');
  }, [
    accessValue,
    developerCoverage,
    displayedSignals,
    evidenceCoverage,
    remediationCoverage,
    review.metrics,
    t,
    verdictLabel,
  ]);

  const handleCopySummary = async () => {
    setCopyState('copying');
    try {
      await navigator.clipboard.writeText(copySummary);
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 2500);
    } catch {
      setCopyState('failed');
      window.setTimeout(() => setCopyState('idle'), 2500);
    }
  };

  return (
    <Card
      className={compact ? 'acc-review-panel acc-review-panel--compact' : 'acc-review-panel'}
      data-testid={dataTestId}
    >
      <div className="acc-review-panel__header">
        <div>
          <div className="acc-review-panel__eyebrow">{t('review.eyebrow')}</div>
          <div className="acc-review-panel__title-row">
            <h2 className="acc-review-panel__title">
              {title ?? (compact ? t('review.reportTitle') : t('review.title'))}
            </h2>
            <Badge variant="neutral">{t('review.badges.internal')}</Badge>
          </div>
          <p className="acc-review-panel__description">
            {description ?? (compact ? t('review.reportDescription') : t('review.description'))}
          </p>
        </div>
        <div className="acc-review-panel__actions">
          <Badge variant={verdictBadgeVariant(review.verdict)}>
            {verdictLabel}
          </Badge>
          <Button type="button" variant="ghost" size="sm" onClick={handleCopySummary}>
            {copyState === 'copying'
              ? t('review.actions.copying')
              : copyState === 'copied'
                ? t('review.actions.copied')
                : copyState === 'failed'
                  ? t('review.actions.copyFailed')
                  : t('review.actions.copySummary')}
          </Button>
        </div>
      </div>

      <RecommendationPanel
        icon={review.verdict === 'likely_reliable' ? '✓' : review.verdict === 'review_recommended' ? '!' : '⚠'}
        title={verdictTitle}
        body={verdictBody}
        variant={verdictPanelVariant(review.verdict)}
        style={{ marginBottom: compact ? 16 : 18 }}
      />

      <div className="acc-review-panel__section-label">{t('review.checklist.title')}</div>
      <div className={compact ? 'acc-review-panel__grid acc-review-panel__grid--compact' : 'acc-review-panel__grid'}>
        <MetricCard
          label={t('review.stats.pagesScanned')}
          value={`${review.metrics.pagesScanned}`}
          hint={
            review.metrics.pagesFailed !== null
              ? t('review.statValues.pageCoverageWithFailures', {
                  failed: review.metrics.pagesFailed,
                  discovered: review.metrics.pagesDiscovered ?? review.metrics.pagesScanned,
                })
              : review.metrics.pagesDiscovered !== null
                ? t('review.statValues.pageCoverage', { discovered: review.metrics.pagesDiscovered })
                : t('review.statValues.pageCoverageUnknown')
          }
        />
        <MetricCard
          label={t('review.stats.issuesFound')}
          value={t('review.statValues.issuesAcrossTypes', {
            issues: review.metrics.totalIssues,
            types: review.metrics.issueTypes,
          })}
          hint={t('review.statValues.severitySummary', review.metrics.severityCounts)}
        />
        <MetricCard
          label={t('review.stats.evidenceCoverage')}
          value={evidenceCoverage}
          hint={review.metrics.issueInstances > 0
            ? t('review.statValues.evidenceCount', {
                captured: Math.round((review.metrics.evidenceCoverage ?? 0) * review.metrics.issueInstances),
                total: review.metrics.issueInstances,
              })
            : t('review.statValues.evidenceFallback')}
        />
        <MetricCard
          label={t('review.stats.remediationCoverage')}
          value={remediationCoverage}
          hint={t('review.stats.developerGuidanceCoverage')}
        />
        {!compact && (
          <>
            <MetricCard
              label={t('review.stats.domSnippetCoverage')}
              value={domSnippetCoverage}
              hint={t('review.stats.selectorCoverage')}
            />
            <MetricCard
              label={t('review.stats.authContext')}
              value={review.metrics.authMode === 'public'
                ? t('review.statValues.public')
                : t('review.statValues.authMode', {
                    mode: t(`review.authModes.${authModeKey(review.metrics.authMode)}`),
                  })}
              hint={accessValue}
            />
          </>
        )}
        {compact && (
          <MetricCard
            label={t('review.stats.authContext')}
            value={review.metrics.authMode === 'public'
              ? t('review.statValues.public')
              : t('review.statValues.authMode', {
                  mode: t(`review.authModes.${authModeKey(review.metrics.authMode)}`),
                })}
            hint={accessValue}
          />
        )}
      </div>

      <div className="acc-review-panel__section-label">{t('review.summary.signals')}</div>
      <div className="acc-review-panel__signals">
        {displayedSignals.length > 0 ? (
          displayedSignals.map((signal) => {
            const content = resolveSignalContent(signal, t);
            return (
              <RecommendationPanel
                key={`${signal.code}-${signal.severity}`}
                icon={signal.severity === 'success' ? '✓' : signal.severity === 'warning' ? '!' : '⚠'}
                title={content.title}
                body={content.body}
                variant={signalRecommendationVariant(signal.severity)}
              />
            );
          })
        ) : (
          <RecommendationPanel
            icon="✓"
            title={t('review.signals.noMajorConcerns.title')}
            body={t('review.signals.noMajorConcerns.body')}
            variant="info"
          />
        )}
      </div>

      {showValidationHelper && (
        <details className="acc-review-helper" data-testid={dataTestId ? `${dataTestId}-helper` : undefined}>
          <summary>{t('review.helper.title')}</summary>
          <p className="acc-review-helper__subtitle">{t('review.helper.subtitle')}</p>
          <div className="acc-review-helper__grid">
            <HelperCard
              title={t('review.helper.goodSite.title')}
              body={t('review.helper.goodSite.body')}
            />
            <HelperCard
              title={t('review.helper.badPage.title')}
              body={t('review.helper.badPage.body')}
            />
            <HelperCard
              title={t('review.helper.authSite.title')}
              body={t('review.helper.authSite.body')}
            />
          </div>
        </details>
      )}
    </Card>
  );
}

function MetricCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="acc-review-metric">
      <div className="acc-review-metric__label">{label}</div>
      <div className="acc-review-metric__value">{value}</div>
      <div className="acc-review-metric__hint">{hint}</div>
    </div>
  );
}

function HelperCard({
  title,
  body,
}: {
  title: string;
  body: string;
}) {
  return (
    <div className="acc-review-helper__card">
      <div className="acc-review-helper__card-title">{title}</div>
      <div className="acc-review-helper__card-body">{body}</div>
    </div>
  );
}

function formatCoverage(value: number | null, t: (key: string, options?: Record<string, unknown>) => string): string {
  if (value === null) {
    return t('review.statValues.notApplicable');
  }
  return t('review.statValues.coveragePercent', { value: Math.round(value * 100) });
}

function authModeKey(mode: string): string {
  switch (mode) {
    case 'bearer_token':
      return 'bearerToken';
    case 'session_storage_state':
      return 'sessionStorageState';
    case 'form_login':
      return 'formLogin';
    case 'public':
    default:
      return 'public';
  }
}

function resolveSignalContent(
  signal: ScanReviewSignal,
  t: (key: string, options?: Record<string, unknown>) => string,
): { title: string; body: string } {
  return {
    title: t(`review.signals.${signal.code}.title`),
    body: t(`review.signals.${signal.code}.body`, signal.data),
  };
}
